import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, LogOut, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { submissionProcessService, authService } from '../services/api';

function parseJson(value, fallback) {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function AdminSubmissionProcess() {
  const navigate = useNavigate();
  const [contentId, setContentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [summary, setSummary] = useState('');
  const [steps, setSteps] = useState([]);
  const [durations, setDurations] = useState([]);

  const [editingSummary, setEditingSummary] = useState(false);
  const [tempSummary, setTempSummary] = useState('');

  const [showStepForm, setShowStepForm] = useState(false);
  const [editingStepIdx, setEditingStepIdx] = useState(null);
  const [stepForm, setStepForm] = useState({ number: '', title: '', description: '', details: '' });

  const [showDurationForm, setShowDurationForm] = useState(false);
  const [editingDurationIdx, setEditingDurationIdx] = useState(null);
  const [durationForm, setDurationForm] = useState({ label: '', value: '' });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await submissionProcessService.get();
        if (response.data && response.data.id) {
          setContentId(response.data.id);
          setSummary(response.data.summary || '');
          setSteps(parseJson(response.data.steps, []));
          setDurations(parseJson(response.data.durations, []));
        }
      } catch (err) {
        setError('خطا در دریافت محتوا');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const saveAll = async (s = summary, st = steps, d = durations) => {
    const data = {
      summary: s,
      steps: JSON.stringify(st),
      durations: JSON.stringify(d),
    };
    try {
      if (contentId) {
        await submissionProcessService.update(contentId, data);
      } else {
        const response = await submissionProcessService.create(data);
        setContentId(response.data.id);
      }
      setError('');
    } catch (err) {
      setError('خطا در ذخیره محتوا');
    }
  };

  const handleSaveSummary = () => {
    setSummary(tempSummary);
    saveAll(tempSummary, steps, durations);
    setEditingSummary(false);
  };

  const handleSaveStep = (e) => {
    e.preventDefault();
    const details = stepForm.details.split('\n').map(line => line.trim()).filter(Boolean);
    const newStep = {
      number: parseInt(stepForm.number) || steps.length + 1,
      title: stepForm.title,
      description: stepForm.description,
      details,
    };
    let updatedSteps;
    if (editingStepIdx !== null) {
      updatedSteps = steps.map((s, i) => i === editingStepIdx ? newStep : s);
    } else {
      updatedSteps = [...steps, newStep];
    }
    setSteps(updatedSteps);
    saveAll(summary, updatedSteps, durations);
    setShowStepForm(false);
    setEditingStepIdx(null);
    setStepForm({ number: '', title: '', description: '', details: '' });
  };

  const handleEditStep = (step, idx) => {
    setStepForm({
      number: step.number,
      title: step.title,
      description: step.description,
      details: (step.details || []).join('\n'),
    });
    setEditingStepIdx(idx);
    setShowStepForm(true);
  };

  const handleDeleteStep = (idx) => {
    const updatedSteps = steps.filter((_, i) => i !== idx);
    setSteps(updatedSteps);
    saveAll(summary, updatedSteps, durations);
  };

  const handleSaveDuration = (e) => {
    e.preventDefault();
    let updatedDurations;
    if (editingDurationIdx !== null) {
      updatedDurations = durations.map((d, i) => i === editingDurationIdx ? durationForm : d);
    } else {
      updatedDurations = [...durations, durationForm];
    }
    setDurations(updatedDurations);
    saveAll(summary, steps, updatedDurations);
    setShowDurationForm(false);
    setEditingDurationIdx(null);
    setDurationForm({ label: '', value: '' });
  };

  const handleEditDuration = (duration, idx) => {
    setDurationForm(duration);
    setEditingDurationIdx(idx);
    setShowDurationForm(true);
  };

  const handleDeleteDuration = (idx) => {
    const updatedDurations = durations.filter((_, i) => i !== idx);
    setDurations(updatedDurations);
    saveAll(summary, steps, updatedDurations);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center h-64">
        <div className="text-gray-500">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت نحوه ارسال مقاله</h1>
              <p className="text-xl text-blue-100">ویرایش مراحل، مدت زمان‌ها و خلاصه</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {/* Steps */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">مراحل ارسال مقاله</h2>
            {!showStepForm && (
              <button onClick={() => setShowStepForm(true)} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">
                <Plus size={20} />
                افزودن مرحله
              </button>
            )}
          </div>

          {showStepForm && (
            <form onSubmit={handleSaveStep} className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">{editingStepIdx !== null ? 'ویرایش مرحله' : 'افزودن مرحله'}</h3>
                <button type="button" onClick={() => { setShowStepForm(false); setEditingStepIdx(null); setStepForm({ number: '', title: '', description: '', details: '' }); }} className="p-2 hover:bg-gray-200 rounded-lg transition">
                  <X size={24} className="text-gray-600" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-900 mb-2">شماره</label>
                  <input type="number" value={stepForm.number} onChange={e => setStepForm({ ...stepForm, number: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-5">
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input type="text" value={stepForm.title} onChange={e => setStepForm({ ...stepForm, title: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-5">
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <input type="text" value={stepForm.description} onChange={e => setStepForm({ ...stepForm, description: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-12">
                  <label className="block text-sm font-bold text-gray-900 mb-2">جزئیات (هر خط یک مورد)</label>
                  <textarea value={stepForm.details} onChange={e => setStepForm({ ...stepForm, details: e.target.value })} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="flex gap-4 mt-4">
                <button type="submit" className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">ذخیره</button>
                <button type="button" onClick={() => { setShowStepForm(false); setEditingStepIdx(null); setStepForm({ number: '', title: '', description: '', details: '' }); }} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {steps.map((step, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-600 text-white rounded-lg w-10 h-10 flex items-center justify-center font-bold flex-shrink-0">{step.number}</div>
                    <div>
                      <h3 className="font-bold text-gray-900">{step.title}</h3>
                      <p className="text-gray-600 text-sm">{step.description}</p>
                      {step.details && (
                        <ul className="list-disc list-inside text-sm text-gray-500 mt-2">
                          {step.details.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditStep(step, idx)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                    <button onClick={() => handleDeleteStep(idx)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">خلاصه فرآیند</h2>
          {editingSummary ? (
            <div className="space-y-4">
              <textarea value={tempSummary} onChange={e => setTempSummary(e.target.value)} rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              <div className="flex gap-4">
                <button onClick={handleSaveSummary} className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
                <button onClick={() => { setTempSummary(summary); setEditingSummary(false); }} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap">{summary || 'خلاصه‌ای ثبت نشده'}</p>
              <button onClick={() => { setEditingSummary(true); setTempSummary(summary); }} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">
                <Edit2 size={18} />
                ویرایش خلاصه
              </button>
            </div>
          )}
        </div>

        {/* Durations */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">مدت زمان‌ها</h2>
            {!showDurationForm && (
              <button onClick={() => setShowDurationForm(true)} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">
                <Plus size={20} />
                افزودن
              </button>
            )}
          </div>

          {showDurationForm && (
            <form onSubmit={handleSaveDuration} className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">{editingDurationIdx !== null ? 'ویرایش مدت زمان' : 'افزودن مدت زمان'}</h3>
                <button type="button" onClick={() => { setShowDurationForm(false); setEditingDurationIdx(null); setDurationForm({ label: '', value: '' }); }} className="p-2 hover:bg-gray-200 rounded-lg transition"><X size={24} className="text-gray-600" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" value={durationForm.label} onChange={e => setDurationForm({ ...durationForm, label: e.target.value })} placeholder="عنوان" required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                <input type="text" value={durationForm.value} onChange={e => setDurationForm({ ...durationForm, value: e.target.value })} placeholder="مقدار (مثلاً 2-4 هفته)" required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="flex gap-4 mt-4">
                <button type="submit" className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">ذخیره</button>
                <button type="button" onClick={() => { setShowDurationForm(false); setEditingDurationIdx(null); setDurationForm({ label: '', value: '' }); }} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
              </div>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {durations.map((duration, idx) => (
              <div key={idx} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">{duration.label}</p>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditDuration(duration, idx)} className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition"><Edit2 size={14} /></button>
                    <button onClick={() => handleDeleteDuration(idx)} className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition"><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="text-lg font-bold text-blue-700">{duration.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminSubmissionProcess;
